import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthHeader } from "services/authHeader";
import { ErrorResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { showNotification } from "shared/slices/statusNotificationsSlice";
import { initialResourceOverviewState, ResourceAvailability, ResourceAvailabilityByFilterRequest, ResourceAvailabilityFilterData, ResourceAvailabilityLegend } from "./resourceAvailabilityInterface";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { buildExportFileName } from "services/exportFileName";
import { API_BASE_URL as baseUri } from "config/env";

export const GetResourceOverviewFilterData = createAsyncThunk<ResourceAvailabilityFilterData, void, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/GetResourceOverviewFilterData',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/resource-availability/getFilterData', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ResourceAvailabilityFilterData;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetResourceOverviewLegend = createAsyncThunk<ResourceAvailabilityLegend, void, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/GetResourceOverviewLegend',
    async (_, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/resource-availability/getLegend', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ResourceAvailabilityLegend;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetResourceAvailabilityByFilter = createAsyncThunk<ResourceAvailability, ResourceAvailabilityByFilterRequest, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/GetResourceAvailabilityByFilter',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/resource-availability/getByFilter', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ResourceAvailability;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ExportResourceAvailability = createAsyncThunk<void, ResourceAvailabilityByFilterRequest, { rejectValue: RejectedValue }>(
    'Slice/TenantSettingSlice/ExportResourceAvailability',
    async (request, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/resource-availability/export', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request),
        });
        if (response.status === 200) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');

            // The server's Content-Disposition name is not in the app's export naming
            // convention, so only its extension is kept.
            const disposition = response.headers.get('Content-Disposition');
            const serverName = disposition ? disposition.split('filename=')[1]?.replace(/"/g, '') : undefined;
            const extension = serverName?.includes('.') ? serverName.split('.').pop() : 'xlsx';
            const filename = `${buildExportFileName('Resource availability')}.${extension}`;

            a.href = url;
            a.download = filename;
            a.click();

            window.URL.revokeObjectURL(url);
            return;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            dispatch(showNotification({ message: errorResponse.detail ?? "Something went wrong. Please try again later.", type: NotificationTypes.Error }));
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const ResourceOverviewSlice = createSlice({
    name: 'ResourceOverviewSlice',
    initialState: initialResourceOverviewState,
    reducers: {
        resetResourceOverview: (state) => {
            state.resourceAvailability = null;
            state.filterData = null;
            state.legend = null;
            state.activeFilter = null;
        },
        setResourceAvailabilityFilter: (state, action: PayloadAction<ResourceAvailabilityByFilterRequest>) => {
            state.activeFilter = action.payload;
        },
    },
    extraReducers(builder) {
        //FILTER DATA
        builder.addCase(GetResourceOverviewFilterData.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(GetResourceOverviewFilterData.fulfilled, (state, action) => {
            state.isLoading = false;
            state.filterData = action.payload;
        });

        //LEGEND
        builder.addCase(GetResourceOverviewLegend.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(GetResourceOverviewLegend.fulfilled, (state, action) => {
            state.isLoading = false;
            state.legend = action.payload;
        });

        //RESOURCE AVAILABILITY
        builder.addCase(GetResourceAvailabilityByFilter.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(GetResourceAvailabilityByFilter.fulfilled, (state, action) => {
            state.isLoading = false;
            state.resourceAvailability = action.payload;
        });

        //EXPORT
        builder.addCase(ExportResourceAvailability.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(ExportResourceAvailability.fulfilled, (state) => {
            state.isLoading = false;
        });
    }
});

export const { resetResourceOverview, setResourceAvailabilityFilter } = ResourceOverviewSlice.actions;
export default ResourceOverviewSlice.reducer;